import { DatePickerController } from '@avanta/shared/components/ui/DatePicker';
import { Input } from '@avanta/shared/components/ui/Input';
import { PhoneInputController } from '@avanta/shared/components/ui/PhoneInput';
import { SelectController } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { EMAIL_PATTERN } from '@avanta/shared/constants/patterns';
import { genderOptions } from '@avanta/shared/types/patients';
import { validateAdultBirthDate } from '@avanta/shared/utils/date';
import { validateNIF } from '@avanta/shared/utils/nif';
import { Building, VenusAndMars } from 'lucide-react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { type InputForm } from 'types/form';

export type AddEmployeeFormValues = {
  email: string;
  firstName: string;
  surname: string;
  nif: string;
  contactPhone: string;
  gender: string;
  dateOfBirth: string;
  centerId: string;
};

export const ADD_EMPLOYEE_FORM_DEFAULT_VALUES: AddEmployeeFormValues = {
  email: '',
  firstName: '',
  surname: '',
  nif: '',
  contactPhone: '',
  gender: '',
  dateOfBirth: '',
  centerId: '',
};

type AddEmployeeInputForm = Omit<InputForm, 'component'> & {
  component:
    | typeof Input
    | typeof PhoneInputController
    | typeof SelectController
    | typeof DatePickerController;
};

export const addEmployeeForm: Record<keyof AddEmployeeFormValues, AddEmployeeInputForm> = {
  firstName: {
    label: 'profile:ProfileSettings.Form.firstName.label',
    name: 'firstName',
    type: 'text',
    placeholder: 'profile:ProfileSettings.Form.firstName.placeholder',
    required: true,
    minLength: {
      value: 3,
      message: 'profile:ProfileSettings.Form.firstName.error',
    },
    component: Input,
  },
  surname: {
    label: 'profile:ProfileSettings.Form.surname.label',
    name: 'surname',
    type: 'text',
    placeholder: 'profile:ProfileSettings.Form.surname.placeholder',
    required: true,
    minLength: {
      value: 3,
      message: 'profile:ProfileSettings.Form.surname.error',
    },
    component: Input,
  },
  email: {
    label: 'auth:SignupForm.Form.email.label',
    name: 'email',
    type: 'email',
    placeholder: 'auth:SignupForm.Form.email.placeholder',
    required: true,
    pattern: {
      value: EMAIL_PATTERN,
      message: 'auth:SignupForm.Form.email.error.invalid',
    },
    component: Input,
  },
  nif: {
    label: 'profile:ProfileSettings.Form.nif.label',
    name: 'nif',
    type: 'text',
    placeholder: 'profile:ProfileSettings.Form.nif.placeholder',
    required: true,
    validate: value => validateNIF(String(value)) || 'profile:ProfileSettings.Form.nif.error',
    component: Input,
  },
  contactPhone: {
    label: 'profile:ProfileSettings.Form.contactPhone.label',
    name: 'contactPhone',
    type: 'phone',
    placeholder: 'profile:ProfileSettings.Form.contactPhone.placeholder',
    required: true,
    component: PhoneInputController,
    validate: (value: string | number) => {
      const phone = String(value || '');
      if (!phone) return `${LOCIZE_NS.COMMON}:Common.required`;
      return isValidPhoneNumber(phone) || `${LOCIZE_NS.COMMON}:Common.phone.error`;
    },
  },
  gender: {
    label: 'profile:ProfileSettings.Form.sex.label',
    name: 'gender',
    placeholder: 'profile:ProfileSettings.Form.sex.placeholder',
    required: true,
    options: genderOptions.slice(0, 2),
    component: SelectController,
    leftIcon: VenusAndMars,
  },
  dateOfBirth: {
    label: 'profile:ProfileSettings.Form.dateOfBirth.label',
    name: 'dateOfBirth',
    placeholder: 'profile:ProfileSettings.Form.dateOfBirth.placeholder',
    required: true,
    validate: validateAdultBirthDate,
    component: DatePickerController,
  },
  centerId: {
    label: 'insurance:AddPatient.Form.centerId.label',
    name: 'centerId',
    placeholder: 'insurance:AddPatient.Form.centerId.placeholder',
    required: true,
    component: SelectController,
    leftIcon: Building,
  },
};
